import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Cpu, Cog, Layers, Radio, Activity, Layers3, FileCode2 } from "lucide-react";
import HexagonField from "@/components/HexagonField";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

const IMG = "https://images.pexels.com/photos/34718925/pexels-photo-34718925.jpeg";

const Step = ({ n, title, copy }) => (
  <div className="relative pl-12">
    <div className="absolute left-0 top-0 w-9 h-9 rounded-full bg-accent flex items-center justify-center font-display font-bold text-sm">
      {n}
    </div>
    <h4 className="font-display text-lg">{title}</h4>
    <p className="text-sm text-white/60 mt-1.5 leading-relaxed">{copy}</p>
  </div>
);

const Cap = ({ icon: Icon, title, items }) => (
  <div className="glass rounded-2xl p-6 hover:border-white/25 transition-colors">
    <Icon className="w-5 h-5 text-accent" />
    <div className="mt-4 font-display text-lg">{title}</div>
    <ul className="mt-3 space-y-1.5">
      {items.map((i) => (
        <li key={i} className="text-sm text-white/60 flex gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-white/40 shrink-0" /> {i}
        </li>
      ))}
    </ul>
  </div>
);

export default function OemOdm() {
  usePageTitle(
    "OEM / ODM — Custom hardware engineering",
    "You bring the idea; we engineer the product. Electronics, firmware, mechanical, and applied ML/AI — end to end, in India, without the enterprise tax. Prototype to production."
  );
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);
  const priceLabel = settings?.oem_hero_price?.replace(/^from\s+/i, "") || "₹25,000";
  return (
    <div>
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-35"
             style={{ backgroundImage: `linear-gradient(0deg, #0A0B10 25%, rgba(10,11,16,0.4) 100%), url(${IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <HexagonField />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-mono">Prime · OEM / ODM</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold mt-5 leading-[1.02] tracking-tighter">
              Your idea, <span className="gradient-text">manufactured</span>.<br />
              Without the enterprise tax.
            </h1>
            <p className="mt-7 text-lg text-white/70 max-w-2xl leading-relaxed">
              You bring the idea. We engineer the product. From sketch to a manufacturable, shippable device —
              electronics, firmware, mechanical, and the intelligence inside it — handled end to end by one team
              in India, without an enterprise budget. Prototype-to-production, starting from <span className="text-white">{priceLabel}</span>.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium hover:shadow-[0_0_30px_rgba(157,76,221,0.5)]">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/founders" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5">
                Meet the team
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">How it ships</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">
            Idea <span className="text-accent">→</span> Design <span className="text-accent">→</span> Prototype <span className="text-accent">→</span> Production.
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-10 max-w-5xl">
            <Step n="01" title="Discovery + Brief" copy="We sit with you, your CAD, your sketches, your spreadsheet. We turn it into an engineering brief and a price." />
            <Step n="02" title="Industrial design + DFM" copy="Form, factor, materials, BOM strategy and manufacturability locked before a single chip is laid out." />
            <Step n="03" title="Electronics + Firmware" copy="PCB design, RF tuning, embedded firmware, peripheral integration. EVT / DVT cycles in weeks not quarters." />
            <Step n="04" title="ML / AI integration" copy="Edge inference, cloud handoff, model retraining — where it makes the product 10× better." />
            <Step n="05" title="Mechanical + Tooling" copy="Enclosure, gaskets, thermals, tolerances. Pilot tooling at Indian vendors we already trust." />
            <Step n="06" title="Pilot manufacturing" copy="20 units, 200 units, 2,000 units. We hand over the dossier, or we keep manufacturing for you." />
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">In-house capability</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter max-w-3xl">
            Six disciplines, <span className="text-accent">one bench</span>.
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Cap icon={Cpu} title="Embedded systems"
                 items={["MCU + MPU based designs", "RTOS / Linux firmware", "Low-power, battery-grade", "OTA + secure boot"]} />
            <Cap icon={Radio} title="RF + wireless"
                 items={["Sub-GHz, Wi-Fi, BLE, Zigbee", "Antenna tuning + FCC pre-comp", "Mesh + gateway architectures", "Experience with RF surveillance systems"]} />
            <Cap icon={Layers} title="PCB + electronics"
                 items={["High-density multilayer", "Mixed-signal, EMI-aware", "Power electronics", "Indian + global sourcing"]} />
            <Cap icon={Cog} title="Mechanical + DFM"
                 items={["Enclosure design", "Tolerance + thermal", "Injection mold-readiness", "Sheet metal / die cast"]} />
            <Cap icon={Activity} title="ML / AI"
                 items={["Edge inference (TinyML)", "Vision, audio, sensor fusion", "Cloud retraining pipelines", "Agentic features"]} />
            <Cap icon={Layers3} title="Pilot manufacturing"
                 items={["Trusted Indian vendor network", "EVT / DVT / PVT cycles", "Quality + compliance dossier", "Continued manufacturing partner"]} />
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="glass rounded-3xl p-10 lg:p-14 relative overflow-hidden noise">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <FileCode2 className="w-6 h-6 text-accent" />
                <h3 className="font-display text-3xl lg:text-4xl mt-4 tracking-tighter">
                  A capability statement, not a portfolio.
                </h3>
                <p className="mt-5 text-white/70 leading-relaxed">
                  We respect client IP — including for the sensitive work we do in RF and applied surveillance.
                  We can demonstrate capability under NDA, but you won't see our clients' IP on this page.
                </p>
              </div>
              <div>
                <Link to="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium hover:shadow-[0_0_30px_rgba(157,76,221,0.5)]">
                  Start an NDA conversation <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
